import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { FileText, AlertCircle } from 'lucide-react';

interface DocumentUsage {
  documentId: string;
  title: string;
  citationCount: number;
  percentage: number;
  avgRelevance: number;
}

interface DocumentEffectivenessChartProps {
  documents: DocumentUsage[];
}

export default function DocumentEffectivenessChart({ documents }: DocumentEffectivenessChartProps) {
  // Prepare data for chart
  const chartData = documents.slice(0, 7).map(doc => ({
    name: doc.title.length > 30 ? `${doc.title.substring(0, 30)  }...` : doc.title,
    citations: doc.citationCount,
    relevance: (doc.avgRelevance * 100).toFixed(0)
  }));

  // Calculate unused documents (mock)
  const totalDocuments = 24; // From mock data
  const usedDocuments = documents.length;
  const unusedDocuments = totalDocuments - usedDocuments;

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-medium text-sm">{label}</p>
          <p className="text-sm">Citations: {payload[0].value}</p>
          <p className="text-sm">Relevance: {payload[0].payload.relevance}%</p>
        </div>
      );
    }
    return null;
  };

  // Colors based on effectiveness
  const getBarColor = (relevance: string) => {
    const rel = parseInt(relevance);
    if (rel >= 90) {
return 'hsl(var(--chart-1))';
}
    if (rel >= 80) {
return 'hsl(var(--chart-2))';
}
    if (rel >= 70) {
return 'hsl(var(--chart-3))';
}
    return 'hsl(var(--chart-4))';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Document Effectiveness</CardTitle>
        <CardDescription>
          Most frequently cited documents in AI responses
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="h-[250px]">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="name"
                className="text-xs"
                tick={{ fill: 'currentColor' }}
                angle={-45}
                textAnchor="end"
                height={100}
              />
              <YAxis
                className="text-xs"
                tick={{ fill: 'currentColor' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="citations" radius={[8, 8, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getBarColor(entry.relevance)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Document Stats */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Document Coverage</span>
            <span className="text-sm text-muted-foreground">
              {usedDocuments}/{totalDocuments} documents used
            </span>
          </div>
          <Progress value={(usedDocuments / totalDocuments) * 100} className="h-2" />
        </div>

        {/* Top Documents List */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Top 3 Documents</p>
          {documents.slice(0, 3).map((doc, index) => (
            <div key={index} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
              <div className="flex items-center space-x-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm truncate max-w-[200px]">{doc.title}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Badge variant="outline" className="text-xs">
                  {doc.citationCount} citations
                </Badge>
                <Badge
                  variant="outline"
                  className={`text-xs ${
                    doc.avgRelevance >= 0.9 ? 'bg-green-100 text-green-800' :
                    doc.avgRelevance >= 0.8 ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}
                >
                  {(doc.avgRelevance * 100).toFixed(0)}% relevant
                </Badge>
              </div>
            </div>
          ))}
        </div>

        {/* Alert for unused documents */}
        {unusedDocuments > 0 && (
          <div className="flex items-start space-x-2 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/10">
            <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                {unusedDocuments} documents never cited
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-200">
                Consider reviewing or removing documents that aren't being used to improve search performance.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}